import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getSubjects, validateSubject } from "../profile.api.js";

/**
 * Shared subject-selection logic for the onboarding wizard (step 3) and the
 * standalone subject popup. Owns the chip list, the suggestion list and the
 * server-side validation call — deliberately NOT the submit, which differs
 * per caller (create-student vs. update-student).
 *
 * @param {string} token  auth token
 * @param {string[]} initialSubjects  subjects to preload the chip list with
 */
const useSubjectSelection = (token, initialSubjects = []) => {
  const [subjects, setSubjects] = useState(() =>
    Array.isArray(initialSubjects) ? [...initialSubjects] : [],
  );
  const [subjectInput, setSubjectInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState("");
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getSubjects(token)
      .then((data) => {
        if (cancelled) return;
        const filtered = (data?.data ?? []).filter(
          (subject) => subject?.name && subject.name.trim() !== "",
        );
        setSuggestions(filtered);
      })
      .catch(() => {
        // Suggestions are optional — the user can still type subjects manually.
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const addSubject = useCallback(
    (subject) => {
      const value = (subject ?? "").trim().toLowerCase();
      if (value === "") return;

      const exists = subjects.some(
        (item) => item.trim().toLowerCase() === value,
      );
      if (exists) {
        toast.dismiss();
        toast.error("Subject already added.", { position: "top-center" });
        return;
      }

      setSubjects([...subjects, value]);
      setError("");
    },
    [subjects],
  );

  const removeSubject = useCallback(
    (subjectToRemove) => {
      const updated = subjects.filter((subject) => subject !== subjectToRemove);
      setSubjects(updated);
      setError(updated.length < 1 ? "Please enter atleast one Subject." : "");
    },
    [subjects],
  );

  /**
   * Runs the server-side subject validation. Surfaces the failure to the user
   * and resolves to false so callers can abort their submit/next step.
   */
  const validate = useCallback(async () => {
    if (!subjects || subjects.length === 0) {
      setError("Please enter atleast one Subject.");
      return false;
    }

    setValidating(true);
    try {
      const message = await validateSubject(subjects, token);
      if (message) {
        toast.dismiss();
        toast.error(message, { position: "top-center" });
        return false;
      }
      return true;
    } catch (err) {
      toast.dismiss();
      toast.error("Something went wrong. Please try again.", {
        position: "top-center",
      });
      return false;
    } finally {
      setValidating(false);
    }
  }, [subjects, token]);

  return {
    subjects,
    setSubjects,
    subjectInput,
    setSubjectInput,
    suggestions,
    error,
    setError,
    validating,
    addSubject,
    removeSubject,
    validate,
  };
};

export default useSubjectSelection;
